<script>
  import { isDarkTheme, librarySoundSelected, userInfo } from "../store";
  import stopIcon from "../assets/stop.svg?raw";
  import pauseIcon from "../assets/pause.svg?raw";
  import playIcon from "../assets/play.svg?raw";
  import tagIcon from "../assets/tag.svg?raw";
  import volumeOffIcon from "../assets/volume-off.svg?raw";
  import volumeOnIcon from "../assets/volume-on.svg?raw";
  import { SortableList } from "@jhubbardsf/svelte-sortablejs";
  import {
    sortableForceFallbackForFirefox,
    sortablePreventDOMManipulationInLibrary,
    stopPropagation,
  } from "../genericHandlers";
  import { fetchAttribution } from "../global";
  import Attribution from "../Attribution.svelte";
  import WaveSurfer from "wavesurfer.js";
  import LibrarySoundFavourite from "./LibrarySoundFavourite.svelte";
  import { libraryState, LibraryState } from "./Library";

  let waveSurferElement = null;
  let publicSound = null;
  let attribution = null;
  let playing = false;
  let paused = false;
  let volume = null;
  let waveSurfer = null;

  export let onSoundStop;

  function isUserSound(publicSound) {
    return publicSound.CreatedByUsername === $userInfo.username;
  }

  const handleOnChooseSound = (evt) => {
    librarySoundSelected.set(publicSound);
  };
  const handleOnUnchooseSound = (evt) => {
    librarySoundSelected.set(null);
    sortablePreventDOMManipulationInLibrary(evt);
  };

  export const playSound = (pS) => {
    stopSound();
    publicSound = pS;
    fetchAttribution(publicSound.Data.id, (att) => (attribution = att));
  };

  function createWavesurfer(url) {
    return WaveSurfer.create({
      container: "#previewer-seeker",
      url: url,
      // Bardic Blue
      waveColor: "#698799",
      progressColor: getBardicDarkOrLightDependingOnColor(),
      barWidth: 6,
      barGap: 1,
      autoplay: false,
      cursorWidth: 0,
      height: "auto",
    });
  }

  $: if (waveSurferElement) {
    if (publicSound) {
      waveSurfer = createWavesurfer(publicSound.Data.datapath);
      waveSurfer.on("ready", () => {
        waveSurfer.play();
        playing = true;
        paused = false;
      });
      waveSurfer.on("finish", () => {
        waveSurfer.setScroll(0);
        waveSurfer.play();
      });
    } else {
      waveSurfer = createWavesurfer("/sound/whisper-trail.ogg");
      waveSurfer.setOptions({
        // Bardic Blue
        progressColor: "#698799",
      });
    }
  }

  $: if (volume && waveSurfer) {
    waveSurfer.setVolume(volume / 100);
  }

  export const stopSound = () => {
    stopAudio();
    publicSound = null;
    onSoundStop();
  };

  const pauseSound = () => {
    if (waveSurfer) {
      waveSurfer.pause();
    }
    paused = true;
  };

  const resumeSound = () => {
    if (waveSurfer) {
      waveSurfer.play();
    }
    paused = false;
  };

  function stopAudio() {
    if (waveSurfer) {
      waveSurfer.stop();
      waveSurfer.destroy();
    }
    playing = false;
  }

  libraryState.subscribe((state) => {
    if (state === LibraryState.Closed) {
      if (waveSurfer !== null) {
        // Sometimes, Svelte's component colleciton kicks in before we do
        stopAudio();
      }
    }
  });

  function getBardicDarkOrLightDependingOnColor() {
    // Dark is --pico-color when mode is dark, light is --bardic-dark
    return $isDarkTheme ? "#c2c7d0" : "#2C303B";
  }

  isDarkTheme.subscribe((isDark) => {
    if (waveSurfer) {
      const color = getBardicDarkOrLightDependingOnColor();
      waveSurfer.setOptions({
        progressColor: color,
      });
    }
  });

  let stringTags;
  $: if (publicSound) {
    stringTags = publicSound.Tags.join(", ");
  } else {
    stringTags = "";
  }
</script>

<SortableList
  class="full-h"
  group={{ name: "sounds-on-lanes", put: false, pull: "clone" }}
  ghostClass="sound-ghost"
  dragClass="sound-ghost"
  handle=".library-preview-drag-handle"
  sort={false}
  onChoose={handleOnChooseSound}
  onEnd={handleOnUnchooseSound}
  forceFallback={sortableForceFallbackForFirefox()}
>
  <div
    class="previewer-container border-solid-2px border-blue border-radius-pico-border-radius padding-1rem full-h"
    class:library-preview-drag-handle={publicSound !== null}
    class:background-color-pico-background={publicSound != null}
    id="library-sound-preview-player"
  >
    <div class="previewer-title font-family-bardic-sans">
      <!-- <div id="previewer-handle-for-tests">
          In playwright tests, we hover at the center of an element to drag and drop it.
          However, it just so happens that the center of every element in the preview is _not_ draggable
          (e.g. the waveform is not draggable, and the cetner of the title falls within the attribution links).
          So, we create a convenience, tiny utility eleement.
      </div> -->
      {#if publicSound}
        <span id="previewer-title-sound-name">
          {publicSound.Name}
        </span>
        <span>
          <Attribution {attribution} isUserSound={isUserSound(publicSound)} />
        </span>
      {:else}
        <span class="blue"><i>Select a sound to preview</i></span>
      {/if}
    </div>
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div
      bind:this={waveSurferElement}
      id="previewer-seeker"
      class="previewer-seeker"
      on:pointerdown={stopPropagation}
      on:mousedown={stopPropagation}
    ></div>
    <div class="previewer-tags sound-attribution font-family-bardic-sans">
      <span class="blue width-1rem">{@html tagIcon}</span><span
        >{stringTags}</span
      >
    </div>
    <div class="library-preview-favorite center-contents-h">
      <LibrarySoundFavourite />
    </div>
    <div
      id="previewer-controls"
      class="previewer-controls center-contents-h flex-direction-row"
    >
      {#if publicSound}
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        {#if playing}
          {#if paused}
            <!-- svelte-ignore a11y-click-events-have-key-events -->
            <div
              id="library-preview-play"
              class="soundcard-button pico-color"
              on:click={resumeSound}
              on:pointerdown={stopPropagation}
              on:mousedown={stopPropagation}
            >
              {@html playIcon}
            </div>
          {:else}
            <!-- svelte-ignore a11y-click-events-have-key-events -->
            <div
              id="library-preview-pause"
              class="soundcard-button pico-color"
              on:click={pauseSound}
              on:pointerdown={stopPropagation}
              on:mousedown={stopPropagation}
            >
              {@html pauseIcon}
            </div>
          {/if}
          <!-- svelte-ignore a11y-click-events-have-key-events -->
          <div
            id="library-preview-stop"
            class="soundcard-button pico-color"
            on:click={stopSound}
            on:pointerdown={stopPropagation}
            on:mousedown={stopPropagation}
          >
            {@html stopIcon}
          </div>
        {:else}
          <div class="soundcard-button" aria-busy="true"></div>
        {/if}
      {:else}
        <button
          id="library-preview-pause-placeholder"
          class="soundcard-button blue"
          type="button"
          on:pointerdown={stopPropagation}
          on:mousedown={stopPropagation}
        >
          {@html pauseIcon}
        </button>
        <button
          id="library-preview-stop-placeholder"
          class="soundcard-button blue"
          type="button"
          on:pointerdown={stopPropagation}
          on:mousedown={stopPropagation}
        >
          {@html stopIcon}
        </button>
      {/if}
    </div>
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div
      class="previewer-volume volume-controls volume-input"
      on:pointerdown={stopPropagation}
      on:mousedown={stopPropagation}
    >
      {@html volumeOffIcon}
      <input
        class="soundcard-volume"
        id="library-preview-volume"
        type="range"
        min="0"
        max="100"
        bind:value={volume}
        on:pointerdown={stopPropagation}
        on:mousedown={stopPropagation}
      />
      {@html volumeOnIcon}
    </div>
  </div>
</SortableList>
